import React, { useState, useEffect } from 'react';
import { Row, Col, InputNumber, Button, TimePicker, message } from 'antd';
import PropTypes from 'prop-types';
import moment from 'moment';
import { get } from 'lodash';
import {
  StyledContainer,
  StyledCapacityTable,
  DayProperty,
  ErrorMessage,
  Days,
  DayColumn,
  ClosedSlot,
  Heading,
} from './styled';
import { updateRules, getRulesByTimetableId } from './actions';
import { prioritizeRules, attachRule } from '$app/shared/helpers/garageTimetable';

const getDatesBetweenPeriod = (start, end) => {
  const now = start.clone(),
    dates = [];

  while (now.isSameOrBefore(end)) {
    dates.push(now.format('YYYY-MM-DD'));
    now.add(1, 'days');
  }
  return dates;
};

function CapacityTable({ garage, getGarage }) {
  const { timetableId, garageId } = garage;
  const [editedDays, setEditedDays] = useState({});
  const [editErrors, setEditErrors] = useState({});

  const [rules, setRules] = useState([]);

  useEffect(() => {
    if (!rules || !rules.length)
      getRulesByTimetableId(timetableId).then((newRules) => {
        if (newRules && newRules.length) {
          const mappedRules = newRules.map((r, index) => ({ ...r, ruleId: index }));
          setRules(mappedRules);
        }
      });
  }, [rules, timetableId]);

  const allDates = getDatesBetweenPeriod(moment(), moment().add(4, 'weeks'));

  const datesWithMetadata = allDates.map((date) => {
    const unprioritizedRules = (rules || []).map((rule) => attachRule(date, rule)).filter((rule) => rule);
    return { date, ...prioritizeRules(unprioritizedRules) };
  });

  const ruleUpdate = (value, ruleId, date, field) => {
    const newValue = typeof value === 'object' ? value : value;
    const currentEditedDays = editedDays;
    const dayToUpdate = {
      ...(currentEditedDays[date] || {}),
      targetRuleId: ruleId || 'no rule id - empty day',
      [field]: newValue,
    };

    /**
     * For validation, we need to check it against data in state, if not there,
     * we compare against values from the initially rendered timetable.
     */
    const uneditedDate = datesWithMetadata.find(({ date: d }) => d === moment(date).format('YYYY-MM-DD'));

    const getComparisonValue = (fieldToCompare) => {
      const val = get(dayToUpdate, fieldToCompare, uneditedDate[fieldToCompare]);
      return fieldToCompare.includes('Time') ? moment(val, 'H:mm') : val;
    };

    setEditedDays({
      ...currentEditedDays,
      [date]: dayToUpdate,
    });

    const saveError = (errorMessage, saveDate, saveField) =>
      setEditErrors((state) => {
        const existingErrorsOnDate = state[saveDate] || {};
        const errorsToSet = {
          ...state,
          [saveDate]: {
            ...existingErrorsOnDate,
            [saveField]: errorMessage,
          },
        };
        if (!errorMessage) {
          delete errorsToSet[saveDate][saveField];
        }

        return errorsToSet;
      });

    if (field === 'startTime') {
      if (newValue.isAfter(getComparisonValue('endTime'))) {
        return saveError(`Opening time can't be after closing time`, date, field);
      }
      if (!newValue.isAfter(getComparisonValue('endTime'))) {
        saveError(null, date, 'startTime');
        saveError(null, date, 'endTime');
      }
    }

    if (field === 'endTime') {
      if (newValue.isBefore(getComparisonValue('startTime'))) {
        return saveError(`Closing time can't be before opening time`, date, field);
      }
      if (!newValue.isBefore(getComparisonValue('startTime'))) {
        saveError(null, date, 'startTime');
        saveError(null, date, 'endTime');
      }
    }

    const commonMaxAllocationErrorText =
      'The sum of the daily MOT + Repairs capacity cannot be less than Daily C&D Capacity';

    if (field === 'maxRepairsCapacity') {
      const maxAllocation = newValue + getComparisonValue('maxMotCapacity');
      if (maxAllocation < getComparisonValue('maxCnd')) {
        return saveError(commonMaxAllocationErrorText, date, field);
      }
      if (maxAllocation >= getComparisonValue('maxCnd')) {
        saveError(null, date, 'maxCnd');
        saveError(null, date, 'maxRepairsCapacity');
      }
    }

    if (field === 'maxMotCapacity') {
      const maxAllocation = newValue + getComparisonValue('maxRepairsCapacity');
      if (maxAllocation < getComparisonValue('maxCnd')) {
        return saveError(commonMaxAllocationErrorText, date, field);
      }
      if (maxAllocation >= getComparisonValue('maxCnd')) {
        saveError(null, date, 'maxCnd');
        saveError(null, date, 'maxMotCapacity');
      }
    }

    if (field === 'maxCnd') {
      const maxAllocation = getComparisonValue('maxRepairsCapacity') + getComparisonValue('maxMotCapacity');
      if (newValue > maxAllocation) {
        return saveError(
          'Daily C&D Capacity cannot exceed the sum of the daily MOT + Repairs capacity',
          date,
          field
        );
      }
      if (newValue <= maxAllocation) {
        saveError(null, date, 'maxCnd');
        saveError(null, date, 'maxAllocation');
      }
    }
  };

  const renderDay = ({
    ruleId,
    date,
    closed,
    startTime,
    endTime,
    maxMotCapacity,
    maxRepairsCapacity,
    maxCnd,
  }) => {
    const isToday = moment(date).isSame(moment(), 'day');
    const dateString = () => (isToday ? 'Today' : moment(date).format('ddd'));
    const handleErrorClass = (property) => {
      if (get(editErrors, date, false)) {
        if (get(editErrors[date], property, false)) return 'error';
      }
    };

    const editedDay = editedDays[date];
    if (editedDay) {
      if (editedDay.maxMotCapacity || editedDay.maxMotCapacity === 0)
        maxMotCapacity = editedDay.maxMotCapacity;
      if (editedDay.maxRepairsCapacity || editedDay.maxRepairsCapacity === 0)
        maxRepairsCapacity = editedDay.maxRepairsCapacity;
      if (editedDay.maxCnd || editedDay.maxCnd === 0) maxCnd = editedDay.maxCnd;
    }

    return (
      <DayColumn xs={3} key={date}>
        <Heading className={isToday && 'today'}>
          {dateString()}
          <br />
          {moment(date).format('D MMM')}
        </Heading>
        {closed ? (
          <ClosedSlot />
        ) : (
          <TimePicker
            className={handleErrorClass('startTime')}
            use12Hours
            format="h:mma"
            defaultValue={moment(startTime, 'H:mm')}
            onChange={(e) => ruleUpdate(e, ruleId, date, 'startTime')}
          />
        )}
        {closed ? (
          <ClosedSlot />
        ) : (
          <TimePicker
            className={handleErrorClass('endTime')}
            use12Hours
            format="h:mma"
            defaultValue={moment(endTime, 'H:mm')}
            onChange={(e) => ruleUpdate(e, ruleId, date, 'endTime')}
          />
        )}
        <InputNumber
          className={handleErrorClass('maxMotCapacity')}
          value={closed ? 0 : maxMotCapacity}
          disabled={closed}
          onChange={(e) => ruleUpdate(e, ruleId, date, 'maxMotCapacity')}
          min={0}
        />
        <InputNumber
          className={handleErrorClass('maxRepairsCapacity')}
          value={closed ? 0 : maxRepairsCapacity}
          disabled={closed}
          onChange={(e) => ruleUpdate(e, ruleId, date, 'maxRepairsCapacity')}
          min={0}
        />
        <InputNumber
          className={handleErrorClass('maxCnd')}
          value={closed ? 0 : maxCnd}
          disabled={closed}
          onChange={(e) => ruleUpdate(e, ruleId, date, 'maxCnd')}
          min={0}
        />
      </DayColumn>
    );
  };

  // if default opening/closing time updated, rerender the table and add rule
  const renderSchedule = (days) => days.map((day) => renderDay(day));

  const getValidationErrors = () =>
    Object.entries(editErrors).filter(([, errors]) => Object.keys(errors).length);

  const hadEdits = Object.keys(editedDays).length > 0;

  const saveRules = () => {
    const rulesToSave = Object.entries(editedDays).map(([date, data]) => {
      const editedDate = moment(date);

      /**
       * A case manager might not edit every single property, but we still need the missing
       * properties to create a rule, we pick them up from `datesWithMetadata`
       * */
      const missingDataSource = datesWithMetadata.find(
        ({ date: d }) => d === editedDate.format('YYYY-MM-DD')
      );
      return {
        description: 'maia_garage_rule',
        // these start and end times need to be formatted as numbers not strings
        year: {
          start: +moment(editedDate).format('YYYY'),
          end: +moment(editedDate).format('YYYY'),
        },
        month: {
          start: +moment(editedDate).format('M'),
          end: +moment(editedDate).format('M'),
        },
        dayOfMonth: {
          start: +moment(editedDate).format('D'),
          end: +moment(editedDate).format('D'),
        },
        hour: {
          start: moment(get(data, 'startTime', missingDataSource.startTime), 'H:mm').format('HH:mm:ss'),
          end: moment(get(data, 'endTime', missingDataSource.endTime), 'H:mm').format('HH:mm:ss'),
        },
        overrideInterval: { start: '00:00', end: '23:59' },
        type: 'repeatableDay',
        metadata: {
          maxMotCapacity: +get(data, 'maxMotCapacity', missingDataSource.maxMotCapacity),
          maxRepairsCapacity: +get(data, 'maxRepairsCapacity', missingDataSource.maxRepairsCapacity),
          maxCnd: +get(data, 'maxCnd', missingDataSource.maxCnd),
        },
        additive: true,
      };
    });

    updateRules(garageId, timetableId, rulesToSave).then((timetable) => {
      getGarage();
      setRules(get(timetable, '[0].rules', []));
      message.info('Timetable rules updated!');
    });
  };

  const uniqueError = Array.from(
    new Set(
      (Object.keys(editErrors) || []).map(
        (date) =>
          editErrors[date].maxCnd ||
          editErrors[date].maxMotCapacity ||
          editErrors[date].maxRepairsCapacity ||
          editErrors[date].startTime ||
          editErrors[date].endTime
      )
    )
  );

  return (
    <StyledContainer className="capacity-table">
      <h2> Daily Capacity Schedule </h2>
      <StyledCapacityTable>
        <Row>
          <Col xs={21}>
            <Days>{renderSchedule(datesWithMetadata)}</Days>
            {uniqueError.length > 0 && uniqueError.map((err) => <ErrorMessage> {err} </ErrorMessage>)}
          </Col>
          <Col xs={3}>
            <DayProperty>Opening Time</DayProperty>
            <DayProperty>Closing Time</DayProperty>
            <DayProperty>Daily MOT Capacity</DayProperty>
            <DayProperty>Daily Repairs Capacity</DayProperty>
            <DayProperty>Daily C&D Capacity</DayProperty>
          </Col>
        </Row>
      </StyledCapacityTable>
      <Button
        type="primary"
        htmlType="submit"
        onClick={saveRules}
        disabled={!hadEdits || getValidationErrors().length}
      >
        Save
      </Button>
    </StyledContainer>
  );
}

CapacityTable.propTypes = {
  garage: PropTypes.object.isRequired,
  getGarage: PropTypes.func.isRequired,
};

export default CapacityTable;
